import {
  Alert,
  Button,
  Card,
  Checkbox,
  Separator,
  HStack,
  Stack,
  Text,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useState, useMemo } from "react";
import { codingExport } from "../../../../project/utils/export-coding-csv";
import { treatmentExport } from "../../../../project/utils/export-treatment-csv";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomDialog } from "../../../../shared/views/CustomDialog";

const i18prefix = "map:export";

enum ExportType {
  Coding = "Coding",
  Treatments = "Treatments",
}

interface Props {
  onClose: () => void;
}
const CsvExportModalBase: React.FC<Props> = ({ onClose }) => {
  const {
    osaNodeStore,
    forecastStore,
    filterStore,
    osaWayStore,
    projectStore,
  } = useStores();

  const selectedProject = projectStore.getSelected();

  const waysWithNodes = useMemo(() => {
    return filterStore.originalOrForecastedOsaWays.filter(
      (way) => osaNodeStore.nodesByWayId.get(way.id)?.length
    );
    // eslint-disable-next-line
  }, [osaWayStore.ways]);

  const [selectedOsaWayIds, setSelectedOsaWayIds] = useState<string[]>(() =>
    waysWithNodes.map((way) => way.id)
  );
  const [exportType, setExportType] = useState<ExportType>();

  const submit = () => {
    switch (exportType) {
      case ExportType.Coding: {
        codingExport(selectedOsaWayIds, forecastStore.computedValues);
        break;
      }
      case ExportType.Treatments: {
        const waystoExport = waysWithNodes.filter((way) =>
          selectedOsaWayIds.includes(way.id)
        );
        treatmentExport(
          selectedProject!,
          waystoExport,
          forecastStore.computedValues?.osaNodes ?? osaNodeStore.nodesByWayId
        );
        break;
      }
    }

    onClose();
  };

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={t(`map:header.export`)}
      footer={
        <HStack>
          <Button colorPalette="primary" variant="ghost" onClick={onClose}>
            {t(`${i18prefix}.close`)}
          </Button>
          <Button
            colorPalette="primary"
            onClick={submit}
            disabled={!selectedOsaWayIds.length || !exportType}
          >
            {t(`${i18prefix}.export`)}
          </Button>
        </HStack>
      }
    >
      <Stack gap={4}>
        {forecastStore.isForecastModeActivated() && (
          <Card.Root variant="outline">
            <Alert.Root status="warning">
              <Alert.Indicator />
              <Text fontSize="sm">{t(`${i18prefix}.forecastWarning`)}</Text>
            </Alert.Root>
          </Card.Root>
        )}
        <NativeSelect.Root>
          <NativeSelect.Field
            placeholder={t(`${i18prefix}.selectDefault`)}
            value={exportType}
            onChange={(e) => {
              const { value } = e.target;
              setExportType(value as ExportType);
            }}
          >
            {Object.values(ExportType).map((type) => (
              <option value={type} key={type}>
                {type}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>

        <Checkbox.Root
          colorPalette="primary"
          checked={selectedOsaWayIds.length === waysWithNodes.length}
          onCheckedChange={({ checked }) => {
            if (checked) {
              setSelectedOsaWayIds(waysWithNodes.map((way) => way.id));
            } else {
              setSelectedOsaWayIds([]);
            }
          }}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>{t(`${i18prefix}.selectAll`)}</Checkbox.Label>
        </Checkbox.Root>
        <Separator />
        {waysWithNodes.map((way) => (
          <Checkbox.Root
            colorPalette="primary"
            key={way.id}
            name={way.id}
            checked={selectedOsaWayIds.includes(way.id)}
            onCheckedChange={({ checked }) => {
              const osaWayId = way.id;
              if (checked) {
                setSelectedOsaWayIds((prev) => [...prev, osaWayId]);
              } else {
                setSelectedOsaWayIds((prev) =>
                  prev.filter((id) => id !== osaWayId)
                );
              }
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>{`${selectedProject!.city}: ${
              way.name
            }`}</Checkbox.Label>
          </Checkbox.Root>
        ))}
      </Stack>
    </CustomDialog>
  );
};

export const CsvExportModal = observer(CsvExportModalBase);
